import React, { useCallback, useEffect, useRef, useState } from "react";
import { Stack, Typography } from "@mui/material";

import { formatDateMmDdYyyy } from "../../helpers/dateHelper";
import { HubButton, HubCardStack, UserDetailIcon } from "../../components";
import { useFormContext, useWatch } from "react-hook-form";
import { getFieldLabel } from "./helper";

const Comment = ({
  sx = {},
  user,
  createdAt,
  fieldKey,
  comment,
  onResolve = () => {},
}) => (
  <HubCardStack padding="20px 15px 12px 15px" gap="10px" sx={sx}>
    <Typography
      sx={{ fontSize: 12, fontWeight: 500, color: "hubColors.mainFocus" }}
    >
      {getFieldLabel(fieldKey)}
    </Typography>
    <UserDetailIcon
      size={16}
      user={{
        name: user?.name,
        email: formatDateMmDdYyyy(createdAt),
        hueShift: user?.id,
      }}
      variant="rounded"
    />
    <Typography sx={{ lineHeight: "14px", fontSize: 14 }}>
      {comment}
    </Typography>
    <Stack width="100%" alignItems="end">
      <HubButton
        label="Resolve"
        variant="text"
        size="small"
        icon="Check"
        iconProps={{ size: 12 }}
        sx={{ "& .MuiStack-root": { gap: "5px" } }}
        onClick={() => onResolve(fieldKey)}
      />
    </Stack>
  </HubCardStack>
);

export const RoutingComments = () => {
  const { control, setValue } = useFormContext();
  const highlightedFields = useWatch({ control, name: "highlightedFields" });
  const fieldsRef = useRef(highlightedFields);
  fieldsRef.current = highlightedFields;

  const [positions, setPositions] = useState([]);
  const rafRef = useRef();

  const computePositions = useCallback(() => {
    const fields = fieldsRef.current;
    if (!fields?.length) {
      setPositions((prev) => (prev.length ? [] : prev));
      return;
    }
    const container = document.getElementById("RoutingTableContainer");
    const table = document.getElementById("RoutingTable");
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const tableWidth = table?.getBoundingClientRect()?.width || 1000;

    const computed = fields
      .filter((f) => f.comment)
      .map((field) => {
        const anchor = document.querySelector(
          `input[name="${field.fieldKey}"]`,
        );
        if (!anchor) return null;

        const anchorRect = anchor.getBoundingClientRect();
        const top =
          anchorRect.top - containerRect.top + container.scrollTop - 21;
        const isFar =
          window.innerWidth - 550 < anchorRect.x + anchorRect.width;

        return {
          ...field,
          top,
          orientation: isFar ? "right" : "left",
          width: `calc(((100vw - 150px - ${tableWidth}px) / 2) - 10px)`,
        };
      })
      .filter(Boolean);

    setPositions(computed);
  }, []);

  const hasComments = !!highlightedFields?.length;

  useEffect(() => {
    if (!hasComments) {
      setPositions((prev) => (prev.length ? [] : prev));
      return undefined;
    }

    const timer = setTimeout(computePositions, 100);
    const container = document.getElementById("RoutingTableContainer");
    const handleUpdate = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(computePositions);
    };

    container?.addEventListener("scroll", handleUpdate);
    window.addEventListener("resize", handleUpdate);
    return () => {
      clearTimeout(timer);
      container?.removeEventListener("scroll", handleUpdate);
      window.removeEventListener("resize", handleUpdate);
      cancelAnimationFrame(rafRef.current);
    };
  }, [hasComments, computePositions]);

  useEffect(() => {
    if (hasComments) computePositions();
  }, [highlightedFields, hasComments, computePositions]);

  const onResolve = useCallback(
    (key) => {
      const current = fieldsRef.current || [];
      const newFields = current.filter(({ fieldKey }) => fieldKey !== key);
      setValue("highlightedFields", newFields, { shouldDirty: true });
    },
    [setValue],
  );

  return (
    <>
      {positions.map(({ fieldKey, top, orientation, width, ...rest }) => (
        <Comment
          {...rest}
          key={fieldKey}
          fieldKey={fieldKey}
          onResolve={onResolve}
          sx={{
            position: "absolute",
            top: `${top}px`,
            [orientation]: "5px",
            width,
          }}
        />
      ))}
    </>
  );
};

export default RoutingComments;
